import React from "react";
import { validateEmail } from "cerulean/src/EmailFormField";

// you might be wondering why we don't use something like formik or @tanstack/react-form for this.
// it's because this logic is tiny and we have simple needs at the moment. the API is modeled pretty
// similar to @tanstack/react-form so if this file is getting complex and we need more form-related features,
// we should be able to migrate easily.

// shared so callers that omit `options` keep a stable memo identity across renders
const NO_FIELD_OPTIONS = {};

export function useFormField<T>(
  defaultValue: T,
  options: { validator?: (value: T) => string | null } = NO_FIELD_OPTIONS
) {
  const [value, setValue] = React.useState(defaultValue);
  const [isDirty, setIsDirty] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  return React.useMemo(
    () => ({
      value,
      error,
      isDefault: defaultValue === value,
      isDirty,
      setValue: (value: React.SetStateAction<T>, dirty = true) => {
        setValue(value);
        setError(null);
        if (dirty) {
          setIsDirty(true);
        }
      },
      setError,
      validate: (silent?: boolean) => {
        if (options.validator) {
          const validation = options.validator(value);
          if (!silent) {
            setError(validation);
          }
          return !validation;
        }
        return true;
      },
      reset: () => {
        setValue(defaultValue);
        setError(null);
        setIsDirty(false);
      },
    }),
    [defaultValue, error, isDirty, options, value]
  );
}

export function useForm(
  fields: Record<
    string,
    {
      isDefault: boolean;
      isDirty: boolean;
      error: string | null;
      setError: (error: string | null) => void;
      validate: (silent?: boolean) => boolean;
      reset: () => void;
    }
  >,
  {
    parent,
    onSubmit,
  }: {
    parent?: {
      isComplete: boolean;
      hasErrors: boolean;
      isValid: () => boolean;
    };
    onSubmit: (options: Record<string, unknown>) => void;
  }
) {
  const [isComplete, setIsComplete] = React.useState(false);

  const submit = React.useCallback(
    (options: Record<string, unknown> = {}) => {
      let valid = true;
      for (const field of Object.values(fields)) {
        if (!field.validate()) {
          valid = false;
        }
      }
      setIsComplete(true);
      if (valid && !parent?.hasErrors) {
        onSubmit(options);
      }
    },
    [fields, parent, onSubmit]
  );

  const handleSubmit = React.useCallback(
    (e: React.FormEvent<HTMLFormElement>, options: Record<string, unknown> = {}) => {
      e.preventDefault();
      submit(options);
    },
    [submit]
  );

  return React.useMemo(
    () => ({
      isComplete,
      isEnabled: !parent || parent.isComplete,
      hasErrors: Object.values(fields).some((field) => field.error) || Boolean(parent?.hasErrors),
      submit,
      handleSubmit,
      setErrors(errors: Record<string, string>) {
        Object.entries(errors).forEach(([key, error]) => {
          fields[key].setError(error);
        });
      },
      isValid() {
        return (
          (!parent || parent.isValid()) &&
          Object.values(fields).every((field) => field.validate(true))
        );
      },
      reset() {
        Object.values(fields).forEach((field) => field.reset());
      },
    }),
    [fields, handleSubmit, isComplete, parent, submit]
  );
}

export function validateRequired(value: string): string | null {
  if (!value.trim()) {
    return "Required.";
  }
  return null;
}

export const validateEmailField = (value: string, required = true): string | null => {
  const trimmedValue = value.trim();

  if (!required && !trimmedValue) {
    return null;
  }

  if (required) {
    const requiredValidation = validateRequired(trimmedValue);
    if (requiredValidation) {
      return requiredValidation;
    }
  }

  return validateEmail(trimmedValue, false, "Please enter a valid email address.") ?? null;
};
